import { takeLatest, put, select, call, all } from 'redux-saga/effects';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { updateAlternatePickupInfo } from 'yoda-interfaces/lib/Order/OrderApi';
import { getStoreById } from 'yoda-interfaces/lib/Order/StoreApi';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import siteAnalyticsAction from 'yoda-site-components/lib/actions/AnalyticsAction';
import User from 'yoda-site-components/lib/helpers/User/User';
import showGlobalLoader from 'yoda-checkout-components/lib/actions/ShowGlobalLoaderAction';
import {
    URL_CHECKOUT_SHIPPING,
    URL_CHECKOUT_PAYMENT,
    URL_REVIEW_PAGE,
} from 'yoda-checkout-components/lib/common/Constants';
import { validateAlternatePickupFormSubmit, getDAFormError } from '../helpers/Utils/Utils';
import * as SameDayPickupActionTypes from '../actionTypes/SameDayPickupActionTypes';
import * as SameDayPickupActions from '../actions/SameDayPickupActions';
import ShippingService from '../services/ShippingService';
// eslint-disable-next-line import/no-cycle
import { initCheckoutSectionFromClientSideActions } from './NewCheckoutSaga';

const orderDetailsSelector = (state) => state.orderDetails;
const bopisSelector = (state) => state.newBOPIS;
const checkoutInfoSelector = (state) => state.checkoutInfo;
const contextSelector = (state) => state.context;

export default function* initBOPISSection() {
    const orderDetailsInfo = yield select(orderDetailsSelector);
    const orderBopisInfo = ShippingService.getOrderBOPISInfo(orderDetailsInfo);
    const storeId = get(orderBopisInfo, 'store.id');
    const checkoutInfo = yield select(checkoutInfoSelector);
    const { isLoadedFromReview } = checkoutInfo;
    let storeInfo = {};
    if (storeId) {
        const fetchStoreInfoPayload = {
            storeId,
        };
        yield put(showGlobalLoader(true));
        const result = yield call(getStoreById, fetchStoreInfoPayload);
        storeInfo = get(result, 'data.stores[0]', {});
        yield put(showGlobalLoader(false));
    }
    let isUserLoggedIn = User.isUserLoggedIn();
    const context = yield select(contextSelector);
    if (__SERVER__) {
        const cookies = get(context, 'headers.cookies');
        isUserLoggedIn = cookies.DP_USER_STATE === '1';
    }
    yield put(
        SameDayPickupActions.initBOPISSectionUI(
            orderDetailsInfo,
            storeInfo,
            isLoadedFromReview,
            isUserLoggedIn
        )
    );
    yield put(showGlobalLoader(false));
}

function* submitBOPISSection() {
    const bopisInfo = yield select(bopisSelector);
    const orderDetailsInfo = yield select(orderDetailsSelector);
    const { showAlternatePickPerson } = bopisInfo;
    const pickupPersonInfo = { ...bopisInfo.pickupPersonInfo };
    const { submitAction } = bopisInfo;
    const orderBopisInfo = ShippingService.getOrderBOPISInfo(orderDetailsInfo);
    pickupPersonInfo.alternatePerson = showAlternatePickPerson;
    const isBopisValueUpdated = ShippingService.isBopisValueUpdated(
        pickupPersonInfo,
        orderBopisInfo.pickup
    );
    if (isBopisValueUpdated) {
        /**
         * Validated only if show alternate person is checked
         */
        const errorInfo = showAlternatePickPerson
            ? validateAlternatePickupFormSubmit(pickupPersonInfo)
            : {};
        if (!isEmpty(errorInfo)) {
            /**
             * Set form error into Alternate Pickup Form
             */
            yield all([
                put(siteAnalyticsAction.triggerFormError(getDAFormError(errorInfo))),
                put(SameDayPickupActions.alternatePickupFormError(errorInfo)),
            ]);
            return;
        }
        const payload = ShippingService.getSubmitBOPISPayload(pickupPersonInfo, orderBopisInfo);
        yield put(showGlobalLoader(true));
        const result = yield call(FactorySaga, updateAlternatePickupInfo, payload);
        yield put(showGlobalLoader(false));
        if (!result.isSuccess) {
            return;
        }
    }
    const redirectURLMap = {
        Shipping: URL_CHECKOUT_SHIPPING,
        Payment: URL_CHECKOUT_PAYMENT,
        Review: URL_REVIEW_PAGE,
    };
    const request = {
        pageName: redirectURLMap[submitAction],
    };
    yield call(initCheckoutSectionFromClientSideActions, request);
}

const watchSubmitBOPISSection = function* watchSubmitBOPISSection() {
    // eslint-disable-line
    yield takeLatest(SameDayPickupActionTypes.SUBMIT_BOPIS_SECTION, submitBOPISSection);
};
watchSubmitBOPISSection.sagaName = 'watchSubmitBOPISSection';

export { watchSubmitBOPISSection };
